/* eslint-disable no-self-assign */
import React, { useEffect, useRef, useState } from 'react';
import { Redirect, withRouter } from 'react-router';
import { ReactSVG } from 'react-svg';
import { NavLink } from 'react-router-dom';
import TextInput from 'components/fields/textInput';
import { routes } from 'router/routes';
import { UserContext } from 'providers/userProvider';
import { startSession } from 'lib/auth/session';
import { getApiData, postData } from 'lib/api/utils';
import logo from 'assets/signinBack.png';
import Loader from 'components/loader/Loader';

import '../styles.scss';

const WRONG_LOGIN = 'The email or password you entered is incorrect';
const GOOGLE_SCRIPT = 'https://accounts.google.com/gsi/client';
/** A browser the user asked to be remembered on; skips the emailed code. */
const DEVICE_TOKEN_KEY = 'deviceToken';

const SignIn = () => {
    const { user, setUser } = React.useContext(UserContext);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [authData, setAuthData] = useState({
        email: '',
        password: '',
    });
    // Set when the password was right and a code was emailed: { challengeId, sentTo }.
    const [codeStep, setCodeStep] = useState(null);
    const [code, setCode] = useState('');
    const [remember, setRemember] = useState(true);
    const [notice, setNotice] = useState('');
    const googleButtonRef = useRef(null);

    const inputDataHandler = (e) => {
        setAuthData({
            ...authData,
            [e.target.name]: e.target.value,
        });
    };

    /** Stores the session and loads the user, once the API has handed over tokens. */
    const finishLogin = async (item) => {
        const canUsePortal = item.roles?.some(
            (role) => role === 'Trainers' || role === 'SubCoaches',
        );

        if (!canUsePortal) {
            setError(WRONG_LOGIN);
            return;
        }

        // Stores token, refresh token, remembered device and roles, sends the token from now on, and loads Account/me.
        const loggedUser = await startSession(item);

        if (loggedUser.success) {
            setUser({
                ...loggedUser.item,
                roles: item.roles,
                isTemporaryPasswordChanged: item.isTemporaryPasswordChanged,
            });
        }
    };

    const loginHandler = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        let { email, password } = authData;

        email = email;
        password = password;

        const response = await postData('Account/login', {
            email,
            password,
            deviceToken: localStorage.getItem(DEVICE_TOKEN_KEY) || undefined,
        });

        setLoading(false);
        if (!response.success) {
            setError(response);
            return;
        }
        if (response.item.codeRequired) {
            setCode('');
            setNotice('');
            setCodeStep({ challengeId: response.item.challengeId, sentTo: response.item.codeSentTo });
            return;
        }
        await finishLogin(response.item);
    };

    const verifyHandler = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        const response = await postData('Account/login/verify-code', {
            challengeId: codeStep.challengeId,
            code: code.trim(),
            rememberDevice: remember,
        });
        setLoading(false);
        if (!response.success) {
            setError(response);
            return;
        }
        await finishLogin(response.item);
    };

    const resendHandler = async () => {
        setError('');
        setNotice('');
        const response = await postData('Account/login/resend-code', { challengeId: codeStep.challengeId });
        if (response.success) {
            setNotice('A new code is on its way.');
        } else {
            setError(response);
        }
    };

    const backToPassword = () => {
        setCodeStep(null);
        setError('');
        setNotice('');
    };

    // Google sign-in appears only when the server has a Google client id.
    useEffect(() => {
        let cancelled = false;

        const onCredential = async ({ credential }) => {
            setLoading(true);
            setError('');
            const response = await postData('Account/login/google', { idToken: credential });
            setLoading(false);
            if (response.success) {
                await finishLogin(response.item);
            } else {
                setError(response);
            }
        };

        const render = (clientId) => {
            if (cancelled || !window.google?.accounts?.id || !googleButtonRef.current) {
                return;
            }
            window.google.accounts.id.initialize({ client_id: clientId, callback: onCredential });
            window.google.accounts.id.renderButton(googleButtonRef.current, {
                theme: 'filled_black',
                size: 'large',
                text: 'signin_with',
                width: 280,
            });
        };

        getApiData('Account/login/options').then((options) => {
            const clientId = options?.item?.googleClientId;
            if (cancelled || !clientId) {
                return;
            }
            if (window.google?.accounts?.id) {
                render(clientId);
                return;
            }
            const script = document.createElement('script');
            script.src = GOOGLE_SCRIPT;
            script.async = true;
            script.onload = () => render(clientId);
            document.head.appendChild(script);
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (user && !user.isTemporaryPasswordChanged) {
        return <Redirect to={routes.FIRST_LOG_IN} />;
    }

    if (user && user.isTemporaryPasswordChanged) {
        return <Redirect to='/' />;
    }

    return (
        <div className='sign-in' style={{ backgroundImage: `url(${logo})` }}>
            {loading ? <Loader /> : ''}
            {codeStep ? (
                <form className='sign-in-form' onSubmit={verifyHandler}>
                    <ReactSVG
                        className='sign-in-form-logo'
                        src='/assets/svg/logo-new.svg'
                    />
                    <div className='sign-in-form-fields'>
                        <p className='sign-in-form-note'>
                            We emailed a 6-digit code to {codeStep.sentTo}. Enter it to finish signing in.
                        </p>
                        <div className='sign-in-form-fields-field'>
                            <TextInput
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder='6-digit code'
                                type='text'
                                name='code'
                                inputMode='numeric'
                                autoComplete='one-time-code'
                                autoFocus
                            />
                        </div>
                        <label className='sign-in-form-remember'>
                            <input
                                type='checkbox'
                                checked={remember}
                                onChange={(e) => setRemember(e.target.checked)}
                            />
                            Remember this browser for 30 days
                        </label>
                        {error ? <p className='error'>{error}</p> : ''}
                        {notice ? <p className='sign-in-form-note'>{notice}</p> : ''}
                        <button
                            type='submit'
                            className='sign-in-form-button'
                            disabled={code.length !== 6}
                        >
                            Verify
                        </button>
                        <div className='sign-in-form-links'>
                            <button type='button' onClick={resendHandler}>Send a new code</button>
                            <button type='button' onClick={backToPassword}>Back</button>
                        </div>
                    </div>
                </form>
            ) : (
                <form className='sign-in-form' onSubmit={loginHandler}>
                    <ReactSVG
                        className='sign-in-form-logo'
                        src='/assets/svg/logo-new.svg'
                    />
                    <div className='sign-in-form-fields'>
                        <div className='sign-in-form-fields-field'>
                            <TextInput
                                value={authData.email}
                                onChange={inputDataHandler}
                                placeholder='Email'
                                type='email'
                                name='email'
                            />
                        </div>
                        <div className='sign-in-form-fields-field'>
                            <TextInput
                                value={authData.password}
                                onChange={inputDataHandler}
                                placeholder='Password'
                                type='password'
                                name='password'
                            />
                        </div>
                        <div className='sign-in-form-forgot'>
                            <NavLink
                                exact
                                className='sign-in-form-forgot-button'
                                to='/forgot-password'
                            >
                                Forgot password?
                            </NavLink>
                        </div>
                        {error ? <p className='error'>{error}</p> : ''}
                        <button
                            type='submit'
                            className='sign-in-form-button'
                        >
                            Log in
                        </button>
                        <div className='sign-in-form-google' ref={googleButtonRef} />
                    </div>
                </form>
            )}
        </div>
    );
};

export default withRouter(SignIn);
